// Shared pure helpers for the phase Workplans (Theme -> Epic -> Task tree).
// The backend stores a flat node list; nesting is derived from parentId here.

export const OWNERS = {
  MH: "Marco",
  WH: "Ward",
  BR: "Vendor", // web team; renamed from Brazil until the vendor is named
  VA: "Hire", // the technical hire (G8)
  AI: "AI",
};
export const OWNER_ORDER = ["MH", "WH", "BR", "VA", "AI"];
export const STATUSES = ["todo", "doing", "done"];
export const STATUS_LABEL = { todo: "Not started", doing: "In progress", done: "Done" };

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

// Build the Theme -> Epic -> Task tree from the flat node list.
export function buildTree(nodes) {
  const list = nodes || [];
  const childrenOf = (id) => list.filter((n) => n.parentId === id).sort(byOrder);
  return list
    .filter((n) => n.level === "theme")
    .sort(byOrder)
    .map((theme) => ({
      ...theme,
      epics: childrenOf(theme.id).map((epic) => ({
        ...epic,
        tasks: childrenOf(epic.id),
      })),
    }));
}

// Every leaf task under a node id (works for theme or epic).
export function leafTasksUnder(nodes, rootId) {
  const list = nodes || [];
  const childrenOf = (id) => list.filter((n) => n.parentId === id);
  const out = [];
  const stack = [...childrenOf(rootId)];
  while (stack.length) {
    const n = stack.pop();
    if (n.level === "task") out.push(n);
    else stack.push(...childrenOf(n.id));
  }
  return out;
}

export function rollupPct(tasks) {
  if (!tasks.length) return 0;
  const done = tasks.filter((t) => t.status === "done").length;
  return Math.round((done / tasks.length) * 100);
}

// One workplan list holds every phase; nodes without a phaseId are Phase 2's.
export function nodesForPhase(nodes, phaseId) {
  return (nodes || []).filter((n) => (n.phaseId ?? 2) === phaseId);
}

export function statusOf(node) {
  return node.status || "todo";
}

// All leaf tasks in the whole plan (for board / owner / summary).
export function allTasks(nodes) {
  return (nodes || []).filter((n) => n.level === "task");
}

export function summaryCounts(nodes) {
  const tasks = allTasks(nodes);
  const done = tasks.filter((t) => t.status === "done").length;
  const doing = tasks.filter((t) => t.status === "doing").length;
  return { total: tasks.length, done, doing, todo: tasks.length - done - doing };
}

// Next status when a checkbox is clicked (todo<->done only).
export function toggledStatus(node) {
  return statusOf(node) === "done" ? "todo" : "done";
}

// Next status when a board card is clicked (cycle todo->doing->done->todo).
export function cycledStatus(node) {
  const i = STATUSES.indexOf(statusOf(node));
  return STATUSES[(i + 1) % STATUSES.length];
}

// Can the current user change this task's status?
// Admin (Marco) can edit anything; reviewer (Ward) only his own tasks.
export function canEditTask(node, isAdmin) {
  if (isAdmin) return true;
  return node.level === "task" && node.owner === "WH";
}

// Week span (1..12) covering a node's descendant tasks, for the timeline.
export function weekSpan(nodes, rootId) {
  const tasks = leafTasksUnder(nodes, rootId);
  const starts = tasks.map((t) => t.startWeek).filter((w) => w != null);
  const ends = tasks.map((t) => t.endWeek).filter((w) => w != null);
  if (!starts.length || !ends.length) return null;
  return { start: Math.min(...starts), end: Math.max(...ends) };
}
